import React from 'react';
import { useParams, Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';

// Blog post data (matching GolfTipsPage)
const golfTips = [
  {
    id: 1,
    slug: 'choosing-the-best-putter',
    title: 'Choosing the Best Putter for Your Game',
    date: 'April 12, 2025',
    image: 'https://images.pexels.com/photos/6974/golf-green-putting-hole.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    excerpt: 'Finding the right putter can dramatically improve your short game. Learn how to select a putter that matches your stroke style and preferences.',
    author: 'Mike Johnson',
    authorTitle: 'PGA Professional',
    content: `
      <p>Putting accounts for roughly 40% of your strokes during a round of golf, making your putter perhaps the most important club in your bag. Yet many golfers spend thousands on new drivers while using the same off-the-shelf putter for years. Finding the right putter can dramatically lower your scores and increase your confidence on the greens.</p>
      
      <h2>Understanding Putter Types</h2>
      
      <p>There are three main putter types, each suited to different stroke styles:</p>
      
      <ul>
        <li><strong>Blade Putters</strong>: Traditional style with weight concentrated at the heel and toe. Best for players with an arcing putting stroke who prefer feel and feedback.</li>
        <li><strong>Mallet Putters</strong>: Larger heads with more forgiveness and alignment aids. Ideal for straight-back-straight-through putting strokes.</li>
        <li><strong>Mid-Mallet Putters</strong>: A hybrid between blade and mallet, offering moderate forgiveness while maintaining good feel.</li>
      </ul>
      
      <h2>Finding the Right Length</h2>
      
      <p>Standard putters range from 33-35 inches, but the correct length depends on your height, stance, and arm length. Here's a quick guide:</p>
      
      <ul>
        <li>If you're under 5'6": Consider a 33-inch putter</li>
        <li>If you're 5'6" to 5'10": A 34-inch putter may work best</li>
        <li>If you're over 5'10": Try a 35-inch putter</li>
      </ul>
      
      <p>The key is making sure you can set your eyes directly over the ball with your arms hanging naturally. Too long or too short a putter forces compensations in your posture that lead to inconsistency.</p>
      
      <h2>Balancing Act: Face Balancing</h2>
      
      <p>Putter face balancing affects how the clubhead rotates during your stroke:</p>
      
      <ul>
        <li><strong>Face-balanced putters</strong>: The face points upward when balanced horizontally. Best for straight-back-straight-through strokes.</li>
        <li><strong>Toe-hang putters</strong>: The toe points downward when balanced. Better for arcing strokes.</li>
      </ul>
      
      <h2>Materials Matter</h2>
      
      <p>The material of your putter face affects both feel and roll:</p>
      
      <ul>
        <li><strong>Metal faces</strong> (steel, aluminum) provide crisp feedback but can feel firm</li>
        <li><strong>Insert faces</strong> (polymer, soft metals) offer softer feel and can help with distance control</li>
      </ul>
      
      <h2>Try Before You Buy</h2>
      
      <p>No amount of reading can replace testing putters yourself. Spend time on a putting green with different models, focusing on:</p>
      
      <ul>
        <li>How the putter feels in your hands</li>
        <li>Whether the alignment aids work for your eye</li>
        <li>Distance control on long putts</li>
        <li>Accuracy on short putts</li>
      </ul>
      
      <p>Consider scheduling a professional putter fitting. Many golf shops now offer high-tech fitting sessions using launch monitors specifically for putting, which can help you identify the ideal specifications for your stroke.</p>
      
      <h2>The Bottom Line</h2>
      
      <p>The best putter is the one that gives you confidence when you stand over a putt. While technology and specifications matter, your comfort and confidence with the putter are paramount. Find one that looks good to your eye, feels good in your hands, and produces consistent results, and you'll be well on your way to lower scores.</p>
    `
  },
  {
    id: 2,
    slug: 'improving-your-chipping-game',
    title: 'Master Your Chipping: Tips for Around the Green',
    date: 'April 5, 2025',
    image: 'https://images.pexels.com/photos/6578275/pexels-photo-6578275.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    excerpt: 'Struggling with your short game? These expert chipping techniques will help you save strokes and build confidence around the green.',
    author: 'Sarah Williams',
    authorTitle: 'Golf Instructor',
    content: `
      <p>The ability to consistently get up-and-down from around the green is what separates great players from good ones. Yet for many amateur golfers, chipping remains a source of frustration, leading to chunked chips, bladed shots, and unnecessary strokes.</p>
      
      <p>The good news is that with the right technique and practice approach, you can transform your chipping into a strength rather than a weakness. Here's a comprehensive guide to mastering your chipping game.</p>
      
      <h2>The Setup: Building a Solid Foundation</h2>
      
      <p>A proper setup is crucial for consistent chip shots:</p>
      
      <ul>
        <li><strong>Narrow stance</strong>: Feet about 6-8 inches apart</li>
        <li><strong>Weight forward</strong>: 60-70% on your lead foot</li>
        <li><strong>Ball position</strong>: Back of center in your stance (opposite your back foot for very tight lies)</li>
        <li><strong>Hands ahead</strong>: Shaft leaning slightly toward the target</li>
        <li><strong>Open stance</strong>: Feet, hips, and shoulders slightly open to target line</li>
      </ul>
      
      <h2>The Chipping Motion: Keep It Simple</h2>
      
      <p>The most common chipping mistakes come from overcomplicating the motion. Think of chipping as a miniature version of your putting stroke, with these key elements:</p>
      
      <ul>
        <li>Use your shoulders to rock the club back and through, minimizing wrist action</li>
        <li>Maintain your weight on your lead foot throughout the stroke</li>
        <li>Keep your lower body quiet and stable</li>
        <li>Accelerate smoothly through impact - never decelerate</li>
        <li>Maintain the shaft lean established at address through impact</li>
      </ul>
      
      <h2>Club Selection: Beyond the Sand Wedge</h2>
      
      <p>Many amateurs default to their sand wedge for all chips, but versatile chippers use multiple clubs:</p>
      
      <ul>
        <li><strong>More loft (SW, LW)</strong>: For soft landings, tight lies, or when you need to clear obstacles</li>
        <li><strong>Mid-irons (7-9)</strong>: For chips with more roll than flight, especially from good lies</li>
        <li><strong>Hybrid or fairway wood</strong>: For bump-and-run shots from the fringe or light rough</li>
      </ul>
      
      <p>A good rule of thumb: choose the club that will land the ball just on the green and let it roll the rest of the way to the hole like a putt.</p>
      
      <h2>Practice Drills That Actually Work</h2>
      
      <h3>1. The Clock Drill</h3>
      
      <p>Place balls in a circle around a hole, starting at about 5 yards away. Chip each ball using the appropriate club and technique for its position. This forces you to adapt to different lies and angles.</p>
      
      <h3>2. Up-and-Down Challenge</h3>
      
      <p>Give yourself 10 random chips around a practice green. Your goal is to get up-and-down (chip and one putt) for 5 out of 10. As you improve, increase the target to 6, 7, and eventually 8 out of 10.</p>
      
      <h3>3. Landing Zone Practice</h3>
      
      <p>Place a towel or alignment stick on the green to mark your landing spot. Practice landing chips on this target, focusing on distance control rather than where the ball eventually finishes.</p>
      
      <h2>Common Chipping Mistakes to Avoid</h2>
      
      <ul>
        <li><strong>Flipping the hands</strong>: This causes both fat and thin contact</li>
        <li><strong>Ball too far forward</strong>: Leads to thin, skulled chips</li>
        <li><strong>Decelerating</strong>: Creates inconsistent contact and distance control</li>
        <li><strong>Lifting to help the ball up</strong>: The loft of the club will get the ball airborne</li>
        <li><strong>Using only one club</strong>: Limits your options around the green</li>
      </ul>
      
      <h2>Final Thoughts</h2>
      
      <p>The key to great chipping is simplicity and consistency. Develop a repeatable technique, learn to use different clubs based on the situation, and practice with purpose. With dedicated practice, you'll start turning bogeys into pars and build confidence in your short game.</p>
      
      <p>Remember: it doesn't have to be perfect, it just has to get on the green with a reasonable chance for a one-putt. Master this skill, and you'll save countless strokes each round.</p>
    `
  },
  {
    id: 3,
    slug: 'driving-distance-secrets',
    title: 'Add 20 Yards to Your Drive: Distance Secrets',
    date: 'March 29, 2025',
    image: 'https://images.pexels.com/photos/6859552/pexels-photo-6859552.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    excerpt: 'Learn the techniques professional golfers use to maximize driving distance without sacrificing accuracy or consistency.',
    author: 'Tom Richards',
    authorTitle: 'Power Driving Specialist',
    content: `
      <p>Every golfer wants to hit the ball farther off the tee. While raw strength plays a role, the longest hitters on tour aren't necessarily the strongest players – they're the most efficient at creating and transferring energy to the ball. The good news? With the right techniques, almost any golfer can add significant distance to their drives.</p>
      
      <h2>The Physics of Distance</h2>
      
      <p>Before diving into techniques, it's important to understand what creates distance:</p>
      
      <ul>
        <li><strong>Club head speed</strong>: The primary factor in distance</li>
        <li><strong>Centeredness of contact</strong>: Hitting the sweet spot maximizes energy transfer</li>
        <li><strong>Launch angle</strong>: Optimal is typically between 12-15 degrees for most players</li>
        <li><strong>Spin rate</strong>: Too much or too little reduces distance</li>
      </ul>
      
      <h2>Setup Adjustments for More Distance</h2>
      
      <h3>1. Widen Your Stance</h3>
      
      <p>A wider stance creates a more stable base for generating power. Position your feet slightly wider than shoulder-width apart, with the ball positioned forward in your stance (opposite the inside of your lead heel).</p>
      
      <h3>2. Tee Height Matters</h3>
      
      <p>Most amateurs tee the ball too low. With today's large driver heads, you should tee the ball so that half of it sits above the crown of the driver when the club is resting on the ground. This promotes an upward angle of attack, which is critical for maximizing distance.</p>
      
      <h3>3. Grip Pressure: Lighter is Better</h3>
      
      <p>Excess tension kills speed. Hold the club with just enough pressure to control it – around a 5 or 6 on a scale of 10. Your wrists need to hinge freely to create lag and whip through impact.</p>
      
      <h2>Swing Techniques for Maximum Power</h2>
      
      <h3>1. The X-Factor: Shoulder-Hip Separation</h3>
      
      <p>The "X-Factor" refers to the differential between shoulder turn and hip turn at the top of the backswing. A greater differential creates more potential energy. Focus on making a full shoulder turn while restricting hip rotation slightly.</p>
      
      <h3>2. Create Lag</h3>
      
      <p>Lag is the angle formed between the shaft and your lead arm during the downswing. It's like a power reserve that's released through impact. Avoid the common amateur mistake of "casting" the club too early, which releases this power before impact.</p>
      
      <h3>3. Sequence Is Everything</h3>
      
      <p>The proper kinetic sequence starts from the ground up:</p>
      
      <ul>
        <li>Start the downswing with a subtle shift to your lead side</li>
        <li>Then rotate your hips toward the target</li>
        <li>Allow your shoulders and arms to follow</li>
        <li>The hands and club arrive last, creating a whip-like effect</li>
      </ul>
      
      <h3>4. Extend Through Impact</h3>
      
      <p>Many distance-challenged golfers stop rotating through impact. Focus on driving your chest toward the target after contact and extending your arms fully. Your belt buckle should face the target at finish.</p>
      
      <h2>Physical Preparation for Power</h2>
      
      <p>Even with perfect technique, physical limitations can restrict your distance potential. These targeted exercises can help:</p>
      
      <ul>
        <li><strong>Medicine ball throws</strong>: For rotational power</li>
        <li><strong>Plyometric jumps</strong>: For explosive leg drive</li>
        <li><strong>Single-arm cable rotations</strong>: For core strength and sequencing</li>
        <li><strong>Speed training with lighter clubs</strong>: To develop fast-twitch muscle fibers</li>
      </ul>
      
      <h2>Equipment Considerations</h2>
      
      <p>Modern equipment can significantly help your distance quest:</p>
      
      <ul>
        <li>Get fitted for a driver with the right loft (many amateurs play too little loft)</li>
        <li>Consider shaft flex carefully - too stiff can reduce distance, while too flexible sacrifices accuracy</li>
        <li>Experiment with driver head weights - lighter can increase swing speed for some players</li>
        <li>Don't overlook the importance of the right ball for your swing speed</li>
      </ul>
      
      <h2>Distance vs. Accuracy: The Trade-off Myth</h2>
      
      <p>Many golfers believe they must sacrifice accuracy for distance, but this is largely a myth. Efficient power generation actually improves consistency. Wild drives typically come from compensations and manipulations in the swing, not from swinging with proper speed.</p>
      
      <h2>Implementation Plan</h2>
      
      <p>Don't try to implement all these changes at once. Start with setup adjustments, then work on one swing change at a time. Use this progression:</p>
      
      <ol>
        <li>Optimize your setup and ball position</li>
        <li>Work on creating more shoulder turn</li>
        <li>Develop the proper downswing sequence</li>
        <li>Finally, focus on extending through impact</li>
      </ol>
      
      <p>Remember: distance comes from efficiency, not from swinging harder. Focus on technique first, and the speed will follow naturally.</p>
    `
  },
  {
    id: 4,
    slug: 'bunker-shot-fundamentals',
    title: 'Bunker Shot Fundamentals: Escape the Sand Every Time',
    date: 'March 22, 2025',
    image: 'https://images.pexels.com/photos/6578391/pexels-photo-6578391.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    excerpt: 'Transform your bunker play from a weakness to a strength with these simple adjustments to your setup and technique.',
    author: 'Lisa Chen',
    authorTitle: 'Short Game Coach',
    content: `<p>Content coming soon</p>`
  },
  {
    id: 5,
    slug: 'mental-game-strategies',
    title: 'Mental Game Strategies for Lower Scores',
    date: 'March 15, 2025',
    image: 'https://images.pexels.com/photos/6975/golf-tee-golf-ball-golf-club.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    excerpt: 'Improve your focus, manage pressure, and develop a winning mindset with these proven mental techniques from sports psychologists.',
    author: 'Dr. James Wilson',
    authorTitle: 'Sports Psychology Consultant',
    content: `<p>Content coming soon</p>`
  },
  {
    id: 6,
    slug: 'putting-stroke-perfection',
    title: 'Perfect Your Putting Stroke: Drills for Consistency',
    date: 'March 8, 2025',
    image: 'https://images.pexels.com/photos/6574926/pexels-photo-6574926.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    excerpt: 'Eliminate the three-putts and develop a repeatable stroke with these practice drills you can do anywhere - even at home!',
    author: 'Robert Taylor',
    authorTitle: 'Putting Specialist',
    content: `<p>Content coming soon</p>`
  }
];

/**
 * Individual Blog Post page component
 */
const BlogPostPage: React.FC = () => {
  const { slug } = useParams();
  const post = golfTips.find(post => post.slug === slug);
  
  if (!post) {
    return (
      <PageLayout 
        title="Article Not Found" 
        backgroundImage="https://images.pexels.com/photos/6571791/pexels-photo-6571791.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
      >
        <div className="blog-post">
          <h2>Sorry, the article you're looking for couldn't be found.</h2>
          <p>Please return to our <Link to="/golf-tips">Golf Tips page</Link> to find more articles.</p>
        </div>
      </PageLayout>
    );
  }
  
  return (
    <PageLayout 
      title={post.title} 
      backgroundImage={post.image}
    >
      <div className="blog-post">
        <div className="blog-post-header">
          <div className="blog-post-meta">
            <span>{post.date}</span>
            <span>|</span>
            <span>By {post.author}, {post.authorTitle}</span>
          </div>
        </div>
        
        <div className="blog-post-content" dangerouslySetInnerHTML={{ __html: post.content }} />
        
        <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
          <Link to="/golf-tips" className="blog-post-link">← Back to All Golf Tips</Link>
        </div>
      </div>
      
      <section style={{ marginTop: '3rem' }}>
        <h2>Related Articles</h2>
        <div className="blog-posts">
          {golfTips.filter(p => p.id !== post.id).slice(0, 2).map(relatedPost => (
            <div key={relatedPost.id} className="blog-post-card">
              <div 
                className="blog-post-image"
                style={{ backgroundImage: `url(${relatedPost.image})` }}
              ></div>
              <div className="blog-post-content">
                <div className="blog-post-date">{relatedPost.date}</div>
                <h3 className="blog-post-title">{relatedPost.title}</h3>
                <p className="blog-post-excerpt">{relatedPost.excerpt}</p>
                <Link to={`/golf-tips/${relatedPost.slug}`} className="blog-post-link">
                  Read Full Article →
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>
    </PageLayout>
  );
};

export default BlogPostPage;
